// src/utils/clientHelpers.js
// Helper centralizado para operacoes com clientes (PostgreSQL + Firestore)

/**
 * Verifica se cliente eh do Firestore (ID string) ou PostgreSQL (ID numerico)
 * @param {string|number} clientId - ID do cliente
 * @returns {boolean} true se for cliente Firestore
 */
export const isFirestoreClient = (clientId) =>
  typeof clientId === 'string' && !Number.isInteger(Number(clientId));

/**
 * Parse checklist do cliente (JSONB no PostgreSQL, objeto no Firestore)
 * @param {Object} client - Objeto cliente
 * @returns {Array} Checklist parseado
 */
export const parseClientChecklist = (client) => {
  try {
    if (client?.default_checklist && typeof client.default_checklist === 'string') {
      return JSON.parse(client.default_checklist);
    }
    if (Array.isArray(client?.default_checklist)) {
      return client.default_checklist;
    }
  } catch (e) {
    console.warn('Failed to parse default_checklist:', e);
  }
  return client?.checklist || [];
};

/**
 * Parse restricted times do cliente (JSONB no PostgreSQL)
 * @param {Object} client - Objeto cliente
 * @returns {Array} Restricted times parseado
 */
export const parseRestrictedTimes = (client) => {
  try {
    if (client?.restricted_times && typeof client.restricted_times === 'string') {
      return JSON.parse(client.restricted_times);
    }
    if (Array.isArray(client?.restricted_times)) {
      return client.restricted_times;
    }
  } catch (e) {
    console.warn('Failed to parse restricted_times:', e);
  }
  return client?.restrictedTimes || [];
};

/**
 * Obtem campo do cliente com fallback PostgreSQL -> Firestore
 * @param {Object} client - Objeto cliente
 * @param {string} pgField - Nome do campo no PostgreSQL
 * @param {string} fbField - Nome do campo no Firestore
 * @returns {any} Valor do campo
 */
export const getClientField = (client, pgField, fbField) =>
  client?.[pgField] ?? client?.[fbField];

/**
 * Obtem duracao do servico (PostgreSQL: service_duration, Firestore: hours)
 * @param {Object} client - Objeto cliente
 * @returns {number} Duracao em horas
 */
export const getServiceDuration = (client) =>
  client?.service_duration ?? client?.hours ?? 0;

/**
 * Obtem preco padrao (PostgreSQL: default_price, Firestore: defaultInvoiceAmount)
 * @param {Object} client - Objeto cliente
 * @returns {number} Preco padrao
 */
export const getDefaultPrice = (client) =>
  client?.default_price ?? client?.defaultInvoiceAmount ?? 0;

/**
 * Obtem metodo de pagamento
 * @param {Object} client - Objeto cliente
 * @returns {string} Metodo de pagamento
 */
export const getPaymentMethod = (client) =>
  client?.payment_method ?? client?.paymentMethod ?? 'cash';

/**
 * Obtem ID do contato Xero
 * @param {Object} client - Objeto cliente
 * @returns {string|null} Xero contact ID
 */
export const getXeroContactId = (client) =>
  client?.xero_contact_id ?? client?.xeroContactId ?? null;

/**
 * Obtem descricao da invoice
 * @param {Object} client - Objeto cliente
 * @returns {string} Descricao da invoice
 */
export const getInvoiceDescription = (client) =>
  client?.invoice_description ?? client?.invoiceDescription ?? '';

// Mapeamento de campos PostgreSQL <-> Firestore para referencia
export const CLIENT_FIELD_MAP = {
  service_duration: 'hours',
  default_price: 'defaultInvoiceAmount',
  payment_method: 'paymentMethod',
  default_checklist: 'checklist',
  xero_contact_id: 'xeroContactId',
  restricted_times: 'restrictedTimes',
  invoice_description: 'invoiceDescription'
};

/**
 * Normaliza cliente para formato consistente
 * @param {Object} client - Objeto cliente (PostgreSQL ou Firestore)
 * @returns {Object} Cliente normalizado
 */
export const normalizeClient = (client) => {
  if (!client) return null;

  return {
    ...client,
    id: client.id,
    name: client.name,
    address: client.address,
    email: client.email,
    phone: client.phone,
    hours: getServiceDuration(client),
    defaultInvoiceAmount: getDefaultPrice(client),
    paymentMethod: getPaymentMethod(client),
    checklist: parseClientChecklist(client),
    restrictedTimes: parseRestrictedTimes(client),
    xeroContactId: getXeroContactId(client),
    invoiceDescription: getInvoiceDescription(client),
    notes: client.notes || ''
  };
};
